import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { PrivyProvider, usePrivy } from '@privy-io/react-auth';
import App from './App';
import LinkEmailPrompt from './components/profile/LinkEmailPrompt';
import { fetchPublicConfig } from './lib/api';
import { markPrivyReady, registerPrivyLogout } from './lib/runtime';
import { setWalletPortalAppId } from './lib/wallet';
import './index.css';

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');
// Narrowed alias so the async boot() closure keeps the non-null type.
const mountNode: HTMLElement = rootEl;

/**
 * Provider-scoped bridge: exposes Privy's imperative logout to non-provider
 * code (the profile modal's sign-out handler) via the runtime helper.
 */
function PrivyBridge(): null {
  const { logout } = usePrivy();
  useEffect(() => {
    registerPrivyLogout(logout);
  }, [logout]);
  return null;
}

/**
 * Boot: fetch the public config first so we can mount Privy with the backend's
 * app id / client id — the provider needs them at mount, and login depends on
 * Privy. If the backend is absent or unreachable (mock mode, offline boot), the
 * app still renders in its public, disconnected mode without Privy.
 */
async function boot(): Promise<void> {
  const config = await fetchPublicConfig().catch(() => null);
  const root = createRoot(mountNode);

  if (!config?.privy_app_id) {
    root.render(
      <StrictMode>
        <App />
      </StrictMode>,
    );
    return;
  }

  markPrivyReady();
  setWalletPortalAppId(config.privy_app_id);
  root.render(
    <StrictMode>
      <PrivyProvider
        appId={config.privy_app_id}
        clientId={config.privy_client_id}
        // Match the working app.bigwater.io PoC: pass only the app id / client
        // id and let the shared Privy dashboard drive login methods + embedded
        // wallet policy. Overriding embeddedWallets here made Privy mint a
        // managed wallet (the modal opens before the user connects, so they
        // momentarily "have no wallet") and show it instead of their Rabby.
        config={{ appearance: { theme: 'light', logo: 'https://bigwater.io/assets/privy-login-logo.png' } }}
      >
        <App />
        <PrivyBridge />
        <LinkEmailPrompt />
      </PrivyProvider>
    </StrictMode>,
  );
}

void boot();
